import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  PauseVideoIcon,
  PlayVideoIcon,
  SoundMuteVideoIcon,
  SoundVideoIcon,
} from '../../assets/svg/SVGComponents';
import {
  CenterButton,
  Container,
  MuteButton,
  ProgressFill,
  ProgressTrack,
  TimeBadge,
  Video,
} from './styles';

interface IPostVideoProps {
  src: string;
  /** Slide visível do carrossel. Fora dele o vídeo pausa. */
  isActive: boolean;
}

/** Fração do player que precisa estar na tela para o vídeo tocar sozinho. */
const VISIBILITY_RATIO = 0.6;

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';

  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

/**
 * Player dos vídeos do feed da comunidade.
 *
 * Deliberadamente diferente do VideoPlayer das aulas: aqui não há velocidade,
 * modo teatro nem tela cheia. O vídeo se comporta como no Instagram — começa
 * mudo sozinho quando entra na tela, repete, e o quadro fica limpo enquanto
 * roda. Quem quiser som ou pausa toca uma vez.
 */
const PostVideo: React.FC<IPostVideoProps> = ({ src, isActive }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  /**
   * Pausa feita pelo usuário. Sem isto, o efeito de autoplay voltaria a dar
   * play no primeiro re-render e o vídeo ignoraria o clique de pausa.
   */
  const pausedByUserRef = useRef(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.intersectionRatio >= VISIBILITY_RATIO),
      { threshold: [0, VISIBILITY_RATIO, 1] },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Autoplay mudo enquanto o slide está ativo e na tela; pausa ao sair. Quem
  // pediu para parar continua parado até rolar para longe e voltar.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const shouldPlay = isActive && isVisible;

    if (!shouldPlay) {
      video.pause();
      pausedByUserRef.current = false;
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (prefersReducedMotion || pausedByUserRef.current) return;

    // O play() pode ser recusado (política de autoplay); silenciamos porque o
    // botão central continua disponível e a falha não é acionável.
    video.play().catch(() => undefined);
  }, [isActive, isVisible]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      pausedByUserRef.current = false;
      video.play().catch(() => undefined);
    } else {
      pausedByUserRef.current = true;
      video.pause();
    }
  }, []);

  const toggleMute = useCallback((event: React.MouseEvent) => {
    // Sem isto o clique no som atravessa e também pausa o vídeo.
    event.stopPropagation();
    setIsMuted(previous => !previous);
  }, []);

  const seekToPointer = useCallback((event: React.PointerEvent) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;

    const { left, width } = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - left) / width, 0), 1);
    video.currentTime = ratio * video.duration;
  }, []);

  const handleTrackPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      seekToPointer(event);
    },
    [seekToPointer],
  );

  const handleTrackPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
      seekToPointer(event);
    },
    [seekToPointer],
  );

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  // Enquanto roda mostramos o que falta, como no Instagram; parado, a duração
  // total, que é a informação útil para decidir se vale assistir.
  const timeLabel = isPlaying
    ? formatTime(duration - currentTime)
    : formatTime(duration);

  return (
    <Container
      ref={containerRef}
      onClick={togglePlay}
      data-testid="post-video"
      aria-label="Vídeo do projeto"
    >
      <Video
        ref={videoRef}
        src={src}
        muted={isMuted}
        loop
        playsInline
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={event => setCurrentTime(event.currentTarget.currentTime)}
        onLoadedMetadata={event => {
          const { duration: total } = event.currentTarget;
          setDuration(Number.isFinite(total) ? total : 0);
        }}
      />

      {duration > 0 && <TimeBadge>{timeLabel}</TimeBadge>}

      <CenterButton
        type="button"
        $visible={!isPlaying}
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pausar vídeo' : 'Reproduzir vídeo'}
      >
        {isPlaying ? (
          <PauseVideoIcon width="1.5rem" height="1.5rem" fill="#fff" />
        ) : (
          <PlayVideoIcon width="1.5rem" height="1.5rem" fill="#fff" />
        )}
      </CenterButton>

      <MuteButton
        type="button"
        onClick={toggleMute}
        aria-label={isMuted ? 'Ativar som' : 'Desativar som'}
      >
        {isMuted ? (
          <SoundMuteVideoIcon width="1rem" height="1rem" fill="#fff" />
        ) : (
          <SoundVideoIcon width="1rem" height="1rem" fill="#fff" />
        )}
      </MuteButton>

      <ProgressTrack
        onPointerDown={handleTrackPointerDown}
        onPointerMove={handleTrackPointerMove}
        onClick={event => event.stopPropagation()}
      >
        <ProgressFill style={{ width: `${progress}%` }} />
      </ProgressTrack>
    </Container>
  );
};

export default PostVideo;
